import { ExtensionNotRegisteredError } from '../errors'
import type { Extension } from './extension'

// The process-wide registry of v2 extensions, keyed by service id. Mirrors the v1 `services` map in
// v1/namespace.ts: a service module registers once at require time (via Flowrunner.createExtension) and the
// entry persists across the require cache, so re-requiring the same module is a cheap no-op. Version
// detection (../index.ts) checks `registry.get(serviceId)` after a module is required — a hit means v2.

export class ExtensionRegistry {
  readonly #extensions = new Map<string, Extension>()

  register(extension: Extension): void {
    this.#extensions.set(extension.id, extension)
  }

  get(id: string): Extension | undefined {
    return this.#extensions.get(id)
  }

  all(): Extension[] {
    return [...this.#extensions.values()]
  }

  /**
   * The extension for `id`, requiring its module first if it hasn't registered yet. Registration is a
   * require-time side effect and the require cache is shared, so this is a no-op once loaded.
   *
   * The shared namespace must already be installed (../namespace) — callers reach here via ../index.ts,
   * which imports it.
   */
  load(id: string, entryPointPath: string): Extension {
    if (!this.get(id)) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      require(entryPointPath)
    }

    const extension = this.get(id)

    if (!extension) {
      throw new ExtensionNotRegisteredError(id, entryPointPath)
    }

    return extension
  }

  // Test-only: clear the registry between isolated cases.
  reset(): void {
    this.#extensions.clear()
  }
}

export const registry = new ExtensionRegistry()

// Function-shaped accessors, kept because they are the package's published surface.
export function getExtension(id: string): Extension | undefined {
  return registry.get(id)
}

export function getAllExtensions(): Extension[] {
  return registry.all()
}

export function resetRegistry(): void {
  registry.reset()
}
